import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { StatementLineKind } from '../../../types/finance';
import { formatStatementPaisa } from '../../../utils/paisa';
import { pdfColors, pdfFontFamily, pdfLetter } from '../../../utils/pdf';

export interface StatementTableLine {
  kind: StatementLineKind;
  label: string;
  /** A quiet second line under the label. */
  hint?: string;
  code?: string;
  depth: number;
  /** Paisa, one per column. */
  amounts?: number[];
}

interface Props {
  /** Heads the label column. */
  title: string;
  columns: string[];
  lines: StatementTableLine[];
  /** Prefix figures with the currency (a reconciliation); statements print bare figures. */
  currency?: string;
}

// Wide enough for a comparison period's heading on one line — "25 FEB 2026 – 30 JUN 2026".
const AMOUNT_W = 136;
const RULE = { borderBottomWidth: 0.5, borderBottomColor: pdfColors.rule, borderBottomStyle: 'solid' } as const;

const styles = StyleSheet.create({
  head: { flexDirection: 'row', backgroundColor: pdfColors.textPrimary, borderRadius: 3 },
  th: { paddingVertical: 6, paddingHorizontal: 8, fontSize: 6.8, fontWeight: 700, color: pdfColors.textOnDark, textTransform: 'uppercase', letterSpacing: pdfLetter.openLabel },
  row: { flexDirection: 'row', alignItems: 'center', ...RULE },
  label: { flex: 1, flexDirection: 'row', paddingVertical: 4.5, paddingRight: 8 },
  // The account code in its own slot, so a long name wraps under itself.
  codeSlot: { width: 30, paddingTop: 1 },
  name: { flex: 1 },
  labelText: { fontSize: 8.5, color: pdfColors.textPrimary },
  code: { fontFamily: pdfFontFamily.mono, fontSize: 7.5, color: pdfColors.textFaint },
  hint: { fontSize: 7, color: pdfColors.textMuted, marginTop: 1 },
  amount: { width: AMOUNT_W, paddingVertical: 4.5, paddingHorizontal: 8, textAlign: 'right', fontSize: 8.5, color: pdfColors.textPrimary },
  zero: { color: pdfColors.textFaint },
  heading: {
    paddingTop: 11, paddingBottom: 4, paddingHorizontal: 8, fontSize: 7.5, fontWeight: 700, color: pdfColors.textPrimary,
    textTransform: 'uppercase', letterSpacing: pdfLetter.openStrong,
    borderBottomWidth: 0.8, borderBottomColor: pdfColors.dividerStrong, borderBottomStyle: 'solid',
  },
  note: { paddingVertical: 4.5, fontSize: 8, fontStyle: 'italic', color: pdfColors.textMuted },
  group: { fontWeight: 600 },
  subtotal: {
    backgroundColor: pdfColors.surfaceSoft,
    borderTopWidth: 0.8, borderTopColor: pdfColors.textFaint, borderTopStyle: 'solid',
  },
  strong: { fontWeight: 700 },
  total: {
    backgroundColor: pdfColors.brandSoft,
    borderTopWidth: 1.2, borderTopColor: pdfColors.textPrimary, borderTopStyle: 'solid',
    borderBottomWidth: 0,
  },
  // The accountant's double rule under a grand total.
  doubleRule: {
    height: 2.6,
    borderTopWidth: 0.8, borderTopColor: pdfColors.textPrimary, borderTopStyle: 'solid',
    borderBottomWidth: 0.8, borderBottomColor: pdfColors.textPrimary, borderBottomStyle: 'solid',
  },
  heavy: { fontWeight: 700 },
});

/**
 * A financial statement as printed: section headings, indented groups and
 * accounts, subtotals over a rule and totals over a double rule, one figure
 * column per period. Negatives in brackets; a zero is a quiet dash.
 */
/** One line of the statement — headings excepted, which travel with the line under them. */
const Line: React.FC<{ line: StatementTableLine; columns: string[]; currency?: string }> = ({ line: l, columns, currency }) => {
  const indent = { paddingLeft: 8 + l.depth * 12 };
  if (l.kind === 'note') return <View style={styles.row}><Text style={[styles.note, indent]}>{l.label}</Text></View>;
  const total = l.kind === 'total';
  const weight = total ? styles.heavy : l.kind === 'subtotal' ? styles.strong : l.kind === 'group' ? styles.group : {};
  return (
    <>
      <View style={[styles.row, l.kind === 'subtotal' ? styles.subtotal : {}, total ? styles.total : {}]}>
        <View style={[styles.label, indent]}>
          {l.code && <Text style={[styles.code, styles.codeSlot]}>{l.code}</Text>}
          <View style={styles.name}>
            <Text style={[styles.labelText, weight]}>{l.label}</Text>
            {l.hint && <Text style={styles.hint}>{l.hint}</Text>}
          </View>
        </View>
        {columns.map((_, k) => {
          const paisa = l.amounts?.[k] ?? 0;
          return (
            <Text key={k} style={[styles.amount, weight, paisa === 0 ? styles.zero : {}]}>
              {paisa === 0 ? '—' : formatStatementPaisa(paisa, currency)}
            </Text>
          );
        })}
      </View>
      {total && <View style={styles.doubleRule} />}
    </>
  );
};

const StatementTable: React.FC<Props> = ({ title, columns, lines, currency }) => (
  <View>
    <View style={styles.head} fixed>
      <Text style={[styles.th, { flex: 1 }]}>{title}</Text>
      {columns.map(c => <Text key={c} style={[styles.th, { width: AMOUNT_W, textAlign: 'right', letterSpacing: 0.3 }]}>{c}</Text>)}
    </View>
    {lines.map((l, i) => {
      // A heading never ends a page alone: it is bound to the line beneath it (if that line isn't a heading too).
      const bound = (k: number) => lines[k]?.kind === 'heading' && !!lines[k + 1] && lines[k + 1].kind !== 'heading';
      if (bound(i)) return null;
      if (l.kind === 'heading') return <Text key={i} style={styles.heading}>{l.label}</Text>;
      const heading = bound(i - 1) ? lines[i - 1] : null;
      return (
        <View key={i} wrap={false}>
          {heading && <Text style={styles.heading}>{heading.label}</Text>}
          <Line line={l} columns={columns} currency={currency} />
        </View>
      );
    })}
  </View>
);

export default StatementTable;
